import React, { useEffect, useState } from "react";
import styles from "./PrinterStatus.module.css";
import { Button, Select, Modal } from "@sainsburys-tech/fable";
import Headerfile from "../../common/Headerfile";
import Footerfile from "../../common/Footerfile";
import { FaCog } from "@react-icons/all-files/fa/FaCog";
import { useGetPostsQuery, usePutDataMutation } from "../../../api/apiConfig";
import Switch from "@mui/material/Switch";
import { alpha, styled } from "@mui/material/styles";
import { pink } from "@mui/material/colors";
import moment from "moment";
import { getLogisticsRole } from "../../../utils/authToken";
import { USER_ROLE } from "../../../constants/roles";
import Unauthorized from "../../unauthorized/Unauthorized";

interface IData {
  StatusCode: number;
  Status: string;
  TotalRecords?: number | null;
  Message?: string;
  Model?: any;
  Value?: string;
}

interface IResponse {
  data: IData;
  TotalRecords?: number | null;
}
interface Location {
  LocationId: string;
  Name: string;
}

interface IState {
  IsRetired: boolean;
  Status: string;
  Configuration: {
    IP: string;
    IsPublished: boolean;
    IsShared: boolean;
    Port: number;
    Type: string;
    LabelMediaType: string;
  };
  Identity: {
    Name: string;
    SBPrinterId: string;
    SerialNumber: string;
    Location: {
      Id: string;
      Name: string;
      WarehouseFloor: string;
      WarehouseLocality: string;
    };
    PrinterModel: {
      Id: string;
      Name: string;
    };
  };
  StatusOverride: {
    IsAllowed: boolean;
    PreventTill: string | null;
    StatusCode: string;
    StatusDescription: string;
    LastUpdate: string;
  };
}
const PrinterStatus: React.FC = () => {
  const [location, setLocation] = useState<Location[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [apiMessage, setApiMessage] = useState<string>("");
  const [apiResp, setApiResp] = useState<string>("");

  const loggedUserRole = getLogisticsRole();

  const isDisabledSettings =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
      loggedUserRole?.includes(USER_ROLE.READ_WRITE)
      ? false
      : true;

  const isDisabled =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
      loggedUserRole?.includes(USER_ROLE.READ_WRITE) ||
      loggedUserRole?.includes(USER_ROLE.READ_ONLY)
      ? false
      : true;

  const [selectedPrinter, setSelectedPrinter] = useState<IState>({
    IsRetired: false,
    Status: "",
    Configuration: {
      IP: "",
      IsPublished: false,
      IsShared: false,
      Port: 0,
      Type: "",
      LabelMediaType: ""
    },
    Identity: {
      Name: "",
      SBPrinterId: "",
      SerialNumber: "",
      Location: {
        Id: "",
        Name: "default",
        WarehouseFloor: "",
        WarehouseLocality: ""
      },
      PrinterModel: {
        Id: "",
        Name: ""
      }
    },
    StatusOverride: {
      IsAllowed: true,
      PreventTill: "",
      StatusCode: "",
      StatusDescription: "",
      LastUpdate: ""
    }
  });

  const [currentPrinterStatus, setCurrentPrinterStatus] = useState<IState>({
    IsRetired: false,
    Status: "",
    Configuration: {
      IP: "",
      IsPublished: false,
      IsShared: false,
      Port: 0,
      Type: "",
      LabelMediaType: ""
    },
    Identity: {
      Name: "",
      SBPrinterId: "",
      SerialNumber: "",
      Location: {
        Id: "",
        Name: "default",
        WarehouseFloor: "",
        WarehouseLocality: ""
      },
      PrinterModel: {
        Id: "",
        Name: ""
      }
    },
    StatusOverride: {
      IsAllowed: true,
      PreventTill: "",
      StatusCode: "",
      StatusDescription: "",
      LastUpdate: ""
    }
  });

  const label = { inputProps: { "aria-label": "Color switch demo" } };

  const {
    isLoading: isLocationLoading,
    //isError: isLocationError,
    isSuccess: isLocationSuccess,
    data: getAllLocations,
    refetch: fetchAllLocations
  } = useGetPostsQuery("locations/GetLocationFromIdentityToken");

  const {
    isLoading: isPrinterListLoading,
    //isError: isPrinterListError,
    isSuccess: isPrinterListSuccess,
    data: printerDataByLocation,
    refetch
  } = useGetPostsQuery(
    `printers/GetByLocationId?LocationId=${selectedLocationId || ""}`
  );

  useEffect(() => {
    fetchAllLocations();
    refetch();
  }, []);

  const [putData /*{ data: postData, error: postError }*/] =
    usePutDataMutation();
  //const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

  const tableMockData = printerDataByLocation?.Model;

  const handleLocationChange = (value: string) => {
    const selectedOption = location.find((loc) => loc.Name === value);
    handleChange("Identity.Location.Name", value);
    handleChange("Identity.Location.Id", selectedOption?.LocationId || "");

    setSelectedLocationId(selectedOption?.LocationId || "");
  };

  useEffect(() => {
    if (isLocationSuccess && getAllLocations?.Model) {
      const validLocations = getAllLocations.Model.filter(
        (location: any): location is Location =>
          location.LocationId &&
          location.Name &&
          location.Status
      );

      const sortedLocations: Location[] = validLocations.sort(
        (a: any, b: any) => a.Name.localeCompare(b.Name)
      );
      setLocation(sortedLocations);
    }
  }, [isLocationSuccess, getAllLocations]);

  const handlePreventTillChange = (value: string) => {
    handleChange("StatusOverride.PreventTill", value);
  };

  const handleSettingsClick = (printerChosen: any) => {
    setSelectedPrinter(printerChosen);
    setCurrentPrinterStatus(printerChosen);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  function isIResponse(response: any): response is IResponse {
    return response.data !== undefined && typeof response.data === "object";
  }

  const hasChanged = () => {
    if (
      selectedPrinter.Status !== currentPrinterStatus?.Status ||
      selectedPrinter?.StatusOverride?.IsAllowed !==
      currentPrinterStatus?.StatusOverride?.IsAllowed
    ) {
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    setIsSaveButtonDisabled(true);
    if (
      selectedPrinter &&
      !selectedPrinter.StatusOverride.IsAllowed &&
      selectedPrinter.StatusOverride.PreventTill
    ) {
      const currentDate = new Date();
      let preventTillDate;

      const addHours = (date: any, hours: any) => {
        return new Date(date.getTime() + hours * 60 * 60 * 1000);
      };

      switch (selectedPrinter.StatusOverride.PreventTill) {
        case "1 hour":
          preventTillDate = addHours(currentDate, 1);
          break;
        case "2 hours":
          preventTillDate = addHours(currentDate, 2);
          break;
        case "3 hours":
          preventTillDate = addHours(currentDate, 3);
          break;
        case "4 hours":
          preventTillDate = addHours(currentDate, 4);
          break;
        case "5 hours":
          preventTillDate = addHours(currentDate, 5);
          break;
        default:
          preventTillDate = null;
      }

      if (preventTillDate) {
        const formattedDate = preventTillDate.toISOString();
        selectedPrinter.StatusOverride.PreventTill = formattedDate;
      }

      if (selectedPrinter.StatusOverride.PreventTill === "Not set") {
        selectedPrinter.StatusOverride.PreventTill = null;
      }
    }

    const updatedPrinter = {
      ...selectedPrinter,
      Status: selectedPrinter.Status,
      StatusOverride: {
        ...selectedPrinter.StatusOverride,
        IsAllowed: selectedPrinter.StatusOverride.IsAllowed,
        PreventTill: selectedPrinter.StatusOverride.PreventTill
      }
    };

    const response = await putData({
      Method: "printers",
      ...updatedPrinter
    });
    if (hasChanged()) {
      setApiResp("Warning");
      setApiMessage("No Changes Made...");
      setIsModalOpen(true);
      setTimeout(() => {
        setApiMessage("");
      }, 2000);
      setIsSaveButtonDisabled(false);
    } else if (
      isIResponse(response) &&
      response.data.StatusCode === 0 &&
      response.data.Status === "Ok"
    ) {
      setApiResp("Success");
      setIsSaveButtonDisabled(false);
      setApiMessage("Updated Successfully!!!");
      setTimeout(() => {
        setIsModalOpen(false);
      }, 1500);
      setTimeout(() => {
        setApiMessage("");
      }, 2000);
      refetch();
    } else {
      setApiResp("Error");
      setIsSaveButtonDisabled(false);
      setApiMessage("Error: Unable to Update..Please try again later.!!!");
      console.log("Error");
      setTimeout(() => {
        setApiMessage("");
      }, 2000);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isModalOpen &&
        !document
          .querySelector(`.${styles.modal}`)
          ?.contains(event.target as Node)
      ) {
        handleCloseModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen]);

  const Android12Switch = styled(Switch)(({ theme }) => ({
    padding: 8,
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#f06c00",
      "&:hover": {
        backgroundColor: alpha(pink[600], theme.palette.action.hoverOpacity)
      }
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#84776c"
    },
    "& .MuiSwitch-track": {
      borderRadius: 22 / 2,
      "&::before, &::after": {
        content: '""',
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: 16,
        height: 16
      },
      "&::before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main)
        )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
        left: 12
      },
      "&::after": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main)
        )}" d="M19,13H5V11H19V13Z" /></svg>')`,
        right: 12
      }
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "none",
      width: 16,
      height: 16,
      margin: 2
    }
  }));

  const handleToggleChange = (name: string) => (event: any) => {
    let value;
    if (name === "Status") {
      value = event.target.checked ? "A" : "I";
    } else {
      value = event.target.checked;
    }
    handleChange(name, value);
  };

  const deepCopy = (obj: any) => {
    return JSON.parse(JSON.stringify(obj));
  };

  const handleChange = (name: string, value: string | boolean | File) => {
    const keys = name.split(".");
    if (keys.length === 1) {
      setSelectedPrinter({ ...selectedPrinter, [name]: value });
    } else if (keys.length === 2) {
      setSelectedPrinter((prevValues) => {
        let updatedValues: any = deepCopy(prevValues);
        let current: any = updatedValues;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return updatedValues;
      });
    } else {
      setSelectedPrinter((prevValues) => {
        let updatedValues: any = { ...prevValues };
        let current: any = updatedValues;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return updatedValues;
      });
    }
  };

  const prefetchTime = (apiTime: any) => {
    if (
      [
        "1 hour",
        "2 hours",
        "3 hours",
        "4 hours",
        "5 hours",
        "Not set"
      ].includes(apiTime)
    ) {
      return apiTime;
    }

    const currentDate = new Date();
    const receivedDate = new Date(apiTime);

    if (isNaN(receivedDate.getTime())) {
      console.error("Invalid date received:", apiTime);
      return "Not set";
    }

    let differenceInMilliseconds =
      currentDate.getTime() - receivedDate.getTime();
    let differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);

    if (differenceInHours < 0) {
      differenceInHours = Math.abs(differenceInHours);
    }

    let dropdownValue;
    if (differenceInHours >= 0.5 && differenceInHours < 1.5) {
      dropdownValue = "1 hour";
    } else if (differenceInHours >= 1.5 && differenceInHours < 2.5) {
      dropdownValue = "2 hours";
    } else if (differenceInHours >= 2.5 && differenceInHours < 3.5) {
      dropdownValue = "3 hours";
    } else if (differenceInHours >= 3.5 && differenceInHours < 4.5) {
      dropdownValue = "4 hours";
    } else if (differenceInHours >= 4.5 && differenceInHours < 5.5) {
      dropdownValue = "5 hours";
    } else {
      dropdownValue = "Not set";
    }

    return dropdownValue;
  };

  return (
    <div data-testid={"PrinterStatusDiv"}>
      <div className={styles.container} data-testid={"PrinterStatusContainer"}>
        <Headerfile />
        {isDisabled ? (
          <Unauthorized />
        ) : (
          <div className={styles.grid}>
            <h4>Printer Status</h4>
            <div className={styles.mainContainer}>
              <Select
                label="Location"
                options={[
                  { label: "Select Location", value: "default" },
                  ...(isLocationLoading
                    ? [
                      {
                        label: "Loading...",
                        value: "loading",
                        isDisabled: isLocationLoading
                      }
                    ]
                    : location?.map((loc) => ({
                      id: loc?.LocationId ?? "",
                      value: loc?.Name ?? "",
                      label: loc?.Name ?? "",
                    })) ?? [])
                ]}
                className={styles.dropdown}
                name="Identity.Location.Name"
                onChange={(value) => handleLocationChange(value)}
                value={selectedPrinter.Identity.Location.Name}
              />
              <br />
              <div className={styles.gridContainer}>
                {selectedLocationId === "" ? (
                  <div className={styles.statusMessage}>
                    Please choose a location
                  </div>
                ) : isPrinterListLoading ||
                  (selectedLocationId !== null && tableMockData == null) ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <div className={styles.loadingText}>Loading...</div>
                  </div>
                ) : tableMockData && tableMockData?.length > 0 ? (
                  tableMockData?.map((item: any, index: any) => (
                    <div key={index} className={styles.gridItem}>
                      {item.Image ? (
                        <img
                          src={`data:image/jpeg;base64,${item.Image}`}
                          alt="Printer"
                          className={styles.profileImage}
                        />
                      ) : (
                        <img
                          alt="Not Available"
                          className={styles.profileImage}
                        />
                      )}
                      <div className={styles.userInfo}>
                        <span>{item.Identity.Name}</span>
                        <div className={styles.statusActive}>
                          <span>Status:</span>
                          {item?.Status === "A" ? (
                            <span className={styles.activeStatus}>
                              Active
                            </span>
                          ) : (
                            <span
                              className={
                                item?.StatusOverride?.StatusDescription !== ""
                                  ? styles.inActiveStatus
                                  : styles.inActiveTextOnly
                              }
                              data-error={
                                item?.StatusOverride?.StatusDescription !== ""
                                  ? item?.StatusOverride?.StatusDescription
                                  : ""
                              }
                            >
                              InActive
                            </span>
                          )}
                        </div>
                      </div>
                      {isDisabledSettings ? (
                        ""
                      ) : (
                        <FaCog
                          className={styles.settingsIcon}
                          onClick={() => handleSettingsClick(item)}
                        />
                      )}
                    </div>
                  ))
                ) : (
                  <p className={styles.statusMessage}>
                    Printers not available for this location
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        {isModalOpen ? (
          <Modal
            isOpen={isModalOpen}
            onClose={() => handleCloseModal()}
            className={styles.modal}
            layout="default"
            header={{
              isDismissible: true
            }}
          >
            <h3>{selectedPrinter?.Identity?.Name}</h3>
            <h4>Manual Override of Printer Status</h4>
            <p className={styles.lastUpdated}>
              Last Updated:{" "}
              {moment(selectedPrinter?.StatusOverride?.LastUpdate).fromNow()}
            </p>
            <div className="text-center">
              <label className={styles.statusLabel}>
                Printer Status:
                <Android12Switch
                  {...label}
                  checked={selectedPrinter?.Status === "I" ? false : true}
                  onChange={handleToggleChange("Status")}
                  className={styles.printerStatusToggle}
                />
              </label>
              {selectedPrinter.Status === "I" &&
                selectedPrinter?.StatusOverride?.StatusDescription ? (
                <div className={styles.currentStatusLabel}>
                  <label>Current Status: </label>
                  <label className={styles.currentStatusLabelText}>
                    {selectedPrinter?.StatusOverride?.StatusDescription}
                  </label>
                </div>
              ) : (
                ""
              )}
              <label className={styles.statusLabel}>
                Allow Override:
                <Android12Switch
                  {...label}
                  checked={selectedPrinter?.StatusOverride?.IsAllowed}
                  onChange={handleToggleChange("StatusOverride.IsAllowed")}
                />
              </label>
              <div className={styles.selectContainer}>
                <Select
                  label="Prevent Until:"
                  options={[
                    { label: "Select a value", value: "default" },
                    { label: "1 hour", value: "1 hour" },
                    { label: "2 hours", value: "2 hours" },
                    { label: "3 hours", value: "3 hours" },
                    { label: "4 hours", value: "4 hours" },
                    { label: "5 hours", value: "5 hours" },
                    { label: "Not set", value: "Not set" }
                  ]}
                  className={styles.preventTillDropDown}
                  name="preventUntill"
                  onChange={(value) => handlePreventTillChange(value)}
                  value={prefetchTime(
                    selectedPrinter?.StatusOverride?.PreventTill
                  )}
                  isDisabled={selectedPrinter.StatusOverride?.IsAllowed}
                />
              </div>
            </div>
            <div className={styles.centerButton}>
              <Button
                className={styles.btnPrimary}
                onClick={handleSave}
                disabled={isSaveButtonDisabled}
              >
                Save
              </Button>
              <br />
              <br />
            </div>
            {
              <p
                className={
                  apiResp === "Success"
                    ? "text-success text-center"
                    : apiResp === "Warning"
                      ? "text-warning text-center"
                      : "text-danger text-center"
                }
                style={{ fontWeight: "600", marginTop: "5%" }}
              >
                {apiMessage}
              </p>
            }
          </Modal>
        ) : (
          ""
        )}
        <Footerfile />
      </div>
    </div>
  );
};

export default PrinterStatus;
